import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { PRODUCTS, PRODUCT_FAMILIES } from "../data/products";
import TypeBadge from "../components/TypeBadge";
import Seo from "../components/Seo";

const FAMILY_ACCENT = { "Available now": "#00E599", "In development": "#FFB259", "Coming soon": "#B98CFF" };

function ProductCard({ p, i }) {
  const available = p.status === "available";
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.06 }}>
      <Link to={`/products/${p.slug}`} data-testid={`product-card-${p.slug}`}
        className="group flex flex-col h-full bg-surface hover:bg-surface-elevated transition-colors">
        <div className="relative aspect-[4/3] border-b border-border overflow-hidden flex items-center justify-center">
          <img src={p.image} alt={p.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-55 group-hover:opacity-75 group-hover:scale-105 transition-all duration-500" />
          <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/30 to-transparent" />
          <span className="absolute top-4 left-4 z-10"><TypeBadge type={p.type} className="backdrop-blur-sm" /></span>
          <span className="relative font-display text-5xl font-medium text-white/80 drop-shadow-lg">{String(i + 1).padStart(2, "0")}</span>
          <span className={`absolute top-4 right-4 px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest border ${available ? "border-primary/50 text-primary bg-primary/10 backdrop-blur-sm" : "border-white/30 text-white/90 bg-black/30 backdrop-blur-sm"}`}>
            {available ? "Available" : "Coming Soon"}
          </span>
        </div>
        <div className="p-7 flex flex-col flex-1">
          <h3 className="font-display text-xl font-medium group-hover:text-primary transition-colors leading-snug">{p.name}</h3>
          <p className="mt-2 text-sm text-foreground/55">{p.descriptor}</p>
          <div className="mt-6 flex items-center justify-between">
            <span className="font-mono text-sm text-foreground/80">{p.price || (available ? "" : "Join Waitlist")}</span>
            <span className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-widest text-foreground/50 group-hover:text-primary transition-colors">
              {available ? "View System" : "Details"} <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default function ProductsPage() {
  return (
    <div data-testid="products-page">
      <Seo title="Products, Systems & Tools" path="/products"
        description="Engineering-based digital systems across three practice areas: environmental, household, and organizational. Includes The Low-Tox Home Blueprint." />
      <section className="grid-bg grain border-b border-border">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
          <p className="mono-label">Products</p>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl tracking-tight max-w-3xl leading-[1.05]">
            Implementation tools, derived from the <span className="text-primary">Greenpapers</span> framework.
          </h1>
          <p className="mt-7 max-w-2xl text-base md:text-lg text-foreground/65 leading-relaxed">
            Structured systems and implementation tools, organized into three practice areas. One engineering
            methodology, applied across environmental, household, and organizational systems.
          </p>
        </div>
      </section>

      {PRODUCT_FAMILIES.map((fam) => {
        const items = PRODUCTS.filter((p) => p.family === fam.key);
        if (items.length === 0) return null;
        const accent = FAMILY_ACCENT[fam.status] || "#00E599";
        return (
          <section key={fam.key} data-testid={`product-family-${fam.key}`} className="border-t border-border first:border-t-0">
            <div className="mx-auto max-w-7xl px-5 sm:px-8 py-14">
              <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
                <div className="max-w-2xl">
                  <div className="flex items-center gap-3">
                    <span className="h-2 w-2 rounded-full" style={{ background: accent }} />
                    <h2 className="font-display text-2xl md:text-3xl tracking-tight">{fam.name}</h2>
                    <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 border rounded-full" style={{ color: accent, borderColor: `${accent}66`, backgroundColor: `${accent}12` }}>{fam.status}</span>
                  </div>
                  <p className="mt-3 text-base text-foreground/65 leading-relaxed">{fam.blurb}</p>
                </div>
                <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground max-w-[220px] leading-relaxed">{fam.future}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border border border-border">
                {items.map((p, i) => <ProductCard key={p.slug} p={p} i={i} />)}
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
}
